/**
 * 案件から見た業務フローのビュー — 変更後 1 枚、または変更前との左右比較。
 *
 * **マスターのビュー（`StoryFlowView`）と分ける。** あちらが描くのは 1 枚だけで、差分の色も
 * 比較の導線も持たない。1 つの部品に両方を入れると、マスターの画面が「比較しない比較画面」に
 * なり、渡さない props が増えるほど何を描く部品なのかが読めなくなる。枠（`flow-workspace/`）と
 * 図の描画エンジン（`business-flow-diagram/`）と詳細（`story-flow-detail.tsx`）は両方で共有するので、
 * 「節点を選ぶと右に説明が出る」「押せば広げられる」は画面で変わらない。
 *
 * **既定は変更前との左右比較で、閉じると変更後の 1 枚になる**（2026-09-25 に決め直した。
 * 開閉は URL の `?compare=0` が持ち、受け口が渡す。差が無ければ受け口が 1 枚に固め、比較の
 * ボタンは押せない — 2026-09-29）。変更前を出すのは、変更後 1 枚に色を塗る
 * 形では**削除された業務を表現できない**ためである。変更前の骨格を丸ごと引けば、消えた業務は
 * 元の位置と元の枝つきで変更前の図に出る。
 *
 * **選んだ節点は面つきで持つ。** 骨格の `nodeId` は業務フローの中で 1 から振る局所 ID なので、
 * 同じ番号が変更前と変更後で別の業務を指しうる。番号だけで持つと、比較に切り替えた瞬間に
 * 別の業務の説明が出る。同じ節点をもう一度押すと右パネルを閉じる。
 *
 * **図の上の文は出さない**（Dhik 2026-09-29「テキスト量が多いことが課題」）。色の凡例は図の枠の
 * ⓘ に、1 枚の表示に出ない削除の件数は「変更前と比較」のボタンを指すと出す断りにする。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件から見た業務フローを、変更後 1 枚または変更前との左右比較として、差分の色と理由つきで描く。
 * @graph-connects computeBusinessFlowLayout [calls] レーン帯と節点の座標の計算
 * @graph-connects FlowWorkspace [calls] 図・詳細・全画面・左右比較の枠
 */
import type { FlowView } from '@cortex/domain-spec-console';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../../components/spec/fallback-states.js';
import { formatPjLabel } from '../../lib/pj-label.js';
import { ComparePaneHeading, FlowCanvas } from '../flow-workspace/flow-canvas.js';
import type { FlowChangeState } from '../flow-workspace/flow-diff-marks.js';
import { FlowWorkspace } from '../flow-workspace/flow-workspace.js';
import { buildBusinessFlowDiagram } from '../business-flow-diagram/diagram.js';
import {
  computeBusinessFlowLayout,
  computeComparedBusinessFlowLayouts,
  firstChangedX,
  initialViewportOf,
  viewportCenteredOn,
  type PlacedDiagramNode,
} from '../business-flow-diagram/layout.js';
import type { StoryFlowPjDiff } from '../pj-story-flow-diff.js';
import { FlowHeading } from './flow-heading.js';
import { FlowInfoMark, flowFrameInfoLines } from './flow-info-mark.js';
import { opensFlowDetail, StoryFlowDetail } from './story-flow-detail.js';

/** 見ている面。 @graph-connects none */
type PjSideName = 'before' | 'after';

/** 図の中で選んだ節点。面と局所 ID の組でしか一意にならない。 @graph-connects none */
interface SelectedStoryNode {
  /** どちらの図で選んだか。 */
  side: PjSideName;
  /** その図での局所 ID。 */
  nodeId: number;
}

/** `PjStoryFlowView` の props。 @graph-connects none */
export interface PjStoryFlowViewProps {
  /** いま適用している案件番号。見出しに出す。 */
  pjNumber: number;
  /** 案件が骨格を変える直前の姿。案件が作った業務フローでは空の骨格になる。 */
  before: FlowView;
  /** 案件を適用した姿。案件が消した業務フローでは空の骨格になる。 */
  after: FlowView;
  /** この案件が業務フローごと消したか。1 枚だけ出すときに、空の変更後の代わりに変更前を出す。 */
  afterMissing: boolean;
  /** 2 枚の突き合わせから導いた差分。 */
  diff: StoryFlowPjDiff;
  /** 業務の id ごとの「なぜそう変えたか」。要求の層から来る。 */
  reasons: ReadonlyMap<number, readonly string[]>;
  /** 変更前と左右に並べているか。URL（`?compare=0`）が持ち、受け口が渡す。 */
  split: boolean;
  /** 変更前と変更後の図に差が無く、比較のボタンを押せないか。受け口が渡す。省けば押せる。 */
  compareDisabled?: boolean;
  /** 左右比較の開閉を切り替える。 */
  onToggleSplit: () => void;
  /** 業務フローの見出しを出すか。ステージの図のように外側が見出しを持つ画面では出さない。 */
  showHeading?: boolean;
}

/** 片側 1 枚を描くのに要るもの。 @graph-connects none */
interface StoryPaneInput {
  /** 面の名前。 */
  side: PjSideName;
  /** その面の骨格。 */
  flow: FlowView;
  /** その面に載せる節点の状態。 */
  stateByNodeId: ReadonlyMap<number, FlowChangeState>;
  /** その面に印を付ける枝の ID。 */
  markedTransitionIds: ReadonlySet<string>;
  /** 印の種類。 */
  markedKind: 'added' | 'removed';
}

/**
 * 案件から見たフローを、見出し・図・詳細に配って描く。
 *
 * @param props 案件番号・変更前後の骨格・未存在の旗・差分・理由
 * @returns 案件から見た業務フロー 1 本
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件から見た業務フローを、名前とエラーだけの見出し・差分の色つきの図・選んだ節点の詳細に配って描く。
 * @graph-connects computeBusinessFlowLayout [calls] レーン帯と節点の座標の計算
 * @graph-connects computeComparedBusinessFlowLayouts [calls] 左右で揃えたレーン帯と節点の座標の計算
 * @graph-connects FlowWorkspace [calls] 図・詳細・全画面・左右比較の枠
 * @graph-connects FlowInfoMark [calls] 図の枠の ⓘ
 * @graph-connects flowFrameInfoLines [calls] 図の枠の ⓘ に束ねる操作の案内と凡例・色の凡例
 * @graph-connects opensFlowDetail [calls] 右パネルを開く節点（業務）かの判定
 * @graph-connects StoryFlowDetail [calls] 業務を選んだときの右パネル
 * @graph-connects ComparePaneHeading [calls] 中身が空の面の左右比較の見出し
 */
export function PjStoryFlowView({
  pjNumber,
  before,
  after,
  afterMissing,
  diff,
  reasons,
  split,
  compareDisabled = false,
  onToggleSplit,
  showHeading = true,
}: PjStoryFlowViewProps) {
  const { t } = useTranslation();
  /*
   * 面の見出しは左右比較の見出し（`flow-workspace/` の面の見出し）と同じ語にする。比較の見出しを
   * 持たない面（中身が空の面）と読み上げの名前もこの語を使う。
   */
  const paneTitle = (side: PjSideName) =>
    side === 'before'
      ? t('pjFlow.beforeTitle')
      : t('pjFlow.afterTitle', { pj: formatPjLabel(pjNumber) });
  const [selected, setSelected] = useState<SelectedStoryNode | null>(null);

  /*
   * 配置は骨格が変わったときだけ計算する。節点を選ぶたびに計算し直すと、結果が同じでも参照が
   * 変わるため図が節点を丸ごと組み直し、選んだ瞬間に図がちらつく。
   */
  /*
   * 左右に並べるときは、2 枚のレーン帯と段数を揃える。各枚が自分の節点だけで帯を組むと、
   * 片方にしか居ないアクターがあったときに同じアクターの帯が左右で違う高さに出る。1 枚だけ
   * 出すときは、その 1 枚の帯だけで組む（もう片方にしか居ない空の帯を足さない）。
   */
  const { before: beforeLayout, after: afterLayout } = useMemo(
    () =>
      split
        ? computeComparedBusinessFlowLayouts(before, after)
        : { before: computeBusinessFlowLayout(before), after: computeBusinessFlowLayout(after) },
    [split, before, after],
  );
  /*
   * 器の高さは 2 枚の高いほうに合わせる。面ごとに高さを変えると、同じ手数の業務が左右で
   * 違う位置に出て、並べて比べる用途に使えない。
   */
  const layoutHeight = Math.max(beforeLayout.height, afterLayout.height);
  /* 縮尺も広いほうの図で 1 つに決める。面ごとに縮めると同じ業務が左右で違う大きさに描かれる。 */
  const canvasWidth = Math.max(beforeLayout.width, afterLayout.width);

  /*
   * 1 枚だけ出すときは変更後を出す。ただし案件が業務フローごと消したなら変更後は空なので、
   * 消えた図が読める変更前を出す。
   */
  const singleSide: PjSideName = afterMissing ? 'before' : 'after';
  const selectedSide = selected?.side ?? singleSide;
  const selectedFlow = selectedSide === 'before' ? before : after;
  const selectedNode =
    selected === null
      ? null
      : (selectedFlow.nodes.find((node) => node.nodeId === selected.nodeId) ?? null);
  const selectedReasons =
    selectedNode?.business === null || selectedNode === null
      ? undefined
      : reasons.get(selectedNode.business.id);

  const panesInput: readonly StoryPaneInput[] = split
    ? [
        {
          side: 'before',
          flow: before,
          stateByNodeId: diff.beforeStateByNodeId,
          markedTransitionIds: diff.removedTransitionIds,
          markedKind: 'removed',
        },
        {
          side: 'after',
          flow: after,
          stateByNodeId: diff.afterStateByNodeId,
          markedTransitionIds: diff.addedTransitionIds,
          markedKind: 'added',
        },
      ]
    : [
        singleSide === 'before'
          ? {
              side: 'before',
              flow: before,
              stateByNodeId: diff.beforeStateByNodeId,
              markedTransitionIds: diff.removedTransitionIds,
              markedKind: 'removed',
            }
          : {
              side: 'after',
              flow: after,
              stateByNodeId: diff.afterStateByNodeId,
              markedTransitionIds: diff.addedTransitionIds,
              markedKind: 'added',
            },
      ];

  /* 見出しは変更後を採る。案件が業務フローごと消したなら変更後は空なので、変更前を採る。 */
  const headingFlow = afterMissing ? before : after;
  /* 名前は変更後が空なら変更前を採る（書き途中の案件は名前をまだ持たないことがある）。 */
  const headingLabel = after.label === '' ? before.label : after.label;

  /*
   * 開いたときは、並べた面のうちいちばん左の変わった節点を枠の中央に置く。読み手が案件モードで
   * 知りたいのは「何が変わったか」で、変わった節点が枠の外にあると何も変わっていない図に見える。
   * 2 面は視点を揃えるので、寄せ先も 2 面を通して 1 つに決める。
   */
  const changedX = firstChangedX(
    panesInput.map((pane) => ({
      placed: (pane.side === 'before' ? beforeLayout : afterLayout).nodes,
      stateByNodeId: pane.stateByNodeId,
    })),
  );
  const initialPaneViewport = (
    placed: readonly PlacedDiagramNode[],
    openNodeId: number | null,
    contentWidth: number,
  ) =>
    openNodeId === null && changedX !== null
      ? viewportCenteredOn(changedX, contentWidth)
      : initialViewportOf(placed, contentWidth, openNodeId);

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      {/*
        図の上は名前とエラー（型の整合の注意・違反の件数）だけにする（Dhik の指示 2026-09-29）。
        差分の件数と「変更前が無い ／ 変更後が無い ／ 流れを変えていない」の注記は出さない。
        変わった業務は図の色が示し、案件が業務フローごと作った ／ 消したときは図の全部が追加 ／
        削除の色になる。案件番号はヘッダーの PJ モードの札が言うので、名前の前に付けない。
        ステージの図に埋めて見出しを外すときも、同じ理由で図の上に何も足さない。
      */}
      {showHeading && <FlowHeading flow={headingFlow} pjNumber={pjNumber} label={headingLabel} />}

      <FlowWorkspace
        quiet
        info={<FlowInfoMark lines={flowFrameInfoLines(t, true)} />}
        layoutHeight={layoutHeight}
        canvasWidth={canvasWidth}
        compare={{
          split,
          disabled: compareDisabled,
          onToggle: onToggleSplit,
          pjNumber,
          /*
            1 枚の表示に削除された業務は出せない。出す場所（変更後の図）にその節点が居ないため。
            何件が隠れていて、どう切り替えれば見えるかを、切り替えのボタンを指したときに言う。
          */
          ...(!split && diff.deletedCount > 0
            ? { title: t('specDiff.deletedHiddenWithCompare', { deleted: diff.deletedCount }) }
            : {}),
        }}
        /*
          両方の骨格が空なら図そのものが無い。拡大も比較もできない面に操作列だけを残すと、
          押しても何も起きないボタンが並ぶ。
        */
        placeholder={
          after.nodes.length === 0 && before.nodes.length === 0 ? (
            <EmptyState message={t('storyFlow.empty')} />
          ) : undefined
        }
        panes={({ paneWidth, height, scale }) =>
          panesInput.map((pane) => {
            const layout = pane.side === 'before' ? beforeLayout : afterLayout;
            if (pane.flow.nodes.length === 0) {
              /*
               * 左右に並べているときは、空の面にも図の面と同じ見出し（変更前は赤・変更後は緑の
               * 「変更前」「変更後」）を付ける。付けないと、片側だけ色の無い見出しになり、どちらが
               * 変更前かを色で読めない。
               */
              return split ? (
                <section
                  key={pane.side}
                  aria-label={paneTitle(pane.side)}
                  data-compare-pane={pane.side}
                  className="min-w-0"
                >
                  <ComparePaneHeading side={pane.side} pjNumber={pjNumber} />
                  <EmptyState message={t('storyFlow.empty')} />
                </section>
              ) : (
                <section key={pane.side} aria-label={paneTitle(pane.side)} className="min-w-0">
                  <EmptyState message={t('storyFlow.empty')} />
                </section>
              );
            }
            const diagram = buildBusinessFlowDiagram({
              flow: pane.flow,
              layout,
              /* 帯は広いほうの図の幅で引く。狭いほうの帯が途中で切れると行が繋がって見えない。 */
              laneWidth: canvasWidth,
              stateByNodeId: pane.stateByNodeId,
              markedTransitionIds: pane.markedTransitionIds,
              markedKind: pane.markedKind,
              selectedNodeId: selectedSide === pane.side ? (selected?.nodeId ?? null) : null,
              onSelect: (nodeId: number) => {
                /* 同じ節点をもう一度押したら閉じる（右パネルの ✕ ・ Esc でも閉じる）。 */
                setSelected((current) =>
                  current?.side === pane.side && current.nodeId === nodeId
                    ? null
                    : { side: pane.side, nodeId },
                );
              },
              t,
            });
            return (
              <FlowCanvas
                key={pane.side}
                side={pane.side}
                title={paneTitle(pane.side)}
                height={height}
                nodes={diagram.nodes}
                edges={diagram.edges}
                laneLabels={diagram.laneLabels}
                /*
                 * 視点は実寸で作り、縮尺は図の面が当てる。寄せ先の計算には、面の幅を実寸へ
                 * 割り戻した値を渡す（縮めた図では同じ面の幅に実寸でより多く入る）。
                 */
                viewportControl={{
                  kind: 'own',
                  initial: initialPaneViewport(
                    layout.nodes,
                    selectedSide === pane.side ? (selected?.nodeId ?? null) : null,
                    paneWidth / scale,
                  ),
                }}
              />
            );
          })
        }
        {...(opensFlowDetail(selectedNode)
          ? {
              detail: (
                <StoryFlowDetail
                  selected={selectedNode}
                  pjNumber={pjNumber}
                  side={selectedSide}
                  {...(selectedReasons === undefined ? {} : { whyChanged: selectedReasons })}
                  onClose={() => {
                    setSelected(null);
                  }}
                />
              ),
            }
          : {})}
      />
    </div>
  );
}
